import { apiScoreCard } from "@/api/recruit/scorecard.api.ts";
import { FormProviderTypeUpdateParams } from "@/components/forms/form-editor.types.ts";
import { SubmitParams } from "@/components/questionnaire/questionnaire.types.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { candidateActionsKeys } from "@/queries/recruit/use-candidate-actions.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import {
  ApiScoreCardPayload,
  ApiScoreCardSearchFilter,
  ApiScoreCardShort,
} from "@/types/recruit/scorecard.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

export const scoreCardsKeys = createQueryKeys("scoreCards", {
  details: (id: number | undefined) => ({
    queryKey: [id],
  }),
  search: (filter: ApiScoreCardSearchFilter) => ({
    queryKey: [filter],
  }),
  formById: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiScoreCard.getFromById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  formByFeedback: (employeeId: number, feedbackId: number) => ({
    queryKey: [employeeId, feedbackId],
    queryFn: async () => {
      const result = await apiScoreCard.getFormByFeedback(employeeId, feedbackId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  resultsForFeedback: (employeeId: number, feedbackId: number) => ({
    queryKey: [employeeId, feedbackId],
    queryFn: async () => {
      const result = await apiScoreCard.getResultsForFeedback(employeeId, feedbackId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  feedbackById: (employeeId: number | undefined, feedbackId: number | undefined) => ({
    queryKey: [employeeId, feedbackId],
    queryFn: async () => {
      if (employeeId == undefined) throw new ApiPassException("employeeId is required");
      if (feedbackId == undefined) throw new ApiPassException("feedbackId is required");

      const result = await apiScoreCard.getFeedback(employeeId, feedbackId);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
  candidateFeedbacksOverview: (candidateId: number | undefined) => ({
    queryKey: [candidateId],
    queryFn: async () => {
      if (candidateId == undefined) throw new ApiPassException("candidateId is required");

      const result = await apiScoreCard.candidateFeedbacksOverview(candidateId);

      if (result.error) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
});

export const useScoreCardDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...scoreCardsKeys.details(id), // Явно указываем queryKey
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Survey id is required");
      const result = await apiScoreCard.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useScoreCardSearch = (filter: ApiScoreCardSearchFilter, enabled: boolean = true) => {
  return useQuery({
    ...scoreCardsKeys.search(filter), // Явно указываем queryKey
    queryFn: async () => {
      const result = await apiScoreCard.search(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useScoreCardSearchInline = (enabled = true) => {
  const [filter, setFilter] = useState<ApiScoreCardSearchFilter>({
    page_number: 1,
    text: undefined,
  });
  const { data, isLoading } = useScoreCardSearch(filter, enabled);
  const options = useMemo(() => {
    return data?.items || ([] as ApiScoreCardShort[]);
  }, [data]);

  function search(text: string | undefined) {
    setFilter({ ...filter, text: text });
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export const useScoreCardSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      id,
      payload,
    }: {
      id?: number | undefined;
      payload?: ApiScoreCardPayload | undefined;
    }) {
      const result =
        id && payload ? await apiScoreCard.edit(id, payload) : await apiScoreCard.create();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return id ? { id } : result.data;
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.details(id).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useScoreCardAddParticipants = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      candidateId,
      scoreCardId,
      employeeIds,
    }: {
      candidateId: number;
      scoreCardId: number;
      employeeIds: number[];
    }) {
      const result = await apiScoreCard.addParticipants(candidateId, scoreCardId, employeeIds);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { ...result.data, candidateId };
    },
    onSuccess: ({ candidateId }) => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.candidateFeedbacksOverview(candidateId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useScoreCardRequestFeedback = () => {
  return useMutation({
    mutationFn: async function ({
      candidateId,
      scoreCardId,
    }: {
      candidateId: number;
      scoreCardId: number;
    }) {
      const result = await apiScoreCard.requestFeedback(candidateId, scoreCardId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useScoreCardDeleteParticipants = () => {
  return useMutation({
    mutationFn: async function ({
      candidateId,
      scoreCardId,
      participantId,
    }: {
      candidateId: number;
      scoreCardId: number;
      participantId: number;
    }) {
      const result = await apiScoreCard.deleteParticipants(candidateId, scoreCardId, participantId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useScoreCardArchiveFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      candidateId,
      feedbackId,
    }: {
      candidateId: number;
      feedbackId: number;
    }) {
      const result = await apiScoreCard.archiveFeedback(candidateId, feedbackId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { candidateId, feedbackId };
    },
    onSuccess: ({ candidateId }) => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.candidateFeedbacksOverview(candidateId).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list(candidateId).queryKey,
      });
    },
  });
};

export const useScoreCardFormById = (id: number, enabled = true) => {
  return useQuery({
    ...scoreCardsKeys.formById(id),
    enabled: !!id && enabled,
  });
};

export const useScoreCardFormUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: FormProviderTypeUpdateParams) {
      const result = await apiScoreCard.formUpdate(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.formById(variables.id).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useScoreCardPublish = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiScoreCard.publish(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.search._def,
      });
    },
  });
};

export const useScoreCardDelete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async function (id: number) {
      const result = await apiScoreCard.delete(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.search._def,
      });
    },
  });
};

export const useScoreCardDuplicate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiScoreCard.duplicate(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.search._def,
      });
    },
    onError: (error: Error) => onError(error),
  });
};

export const useScoreCardFeedbackById = (
  { employeeId, feedbackId }: { employeeId: number | undefined; feedbackId: number | undefined },
  enabled = true
) => {
  return useQuery({
    ...scoreCardsKeys.feedbackById(employeeId, feedbackId),
    enabled: !!employeeId && !!feedbackId && enabled,
  });
};

export const useScoreCardFeedbackSaveDraft = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ employeeId, questionnaireId, answers }: SubmitParams) {
      const result = await apiScoreCard.saveDraft(employeeId, questionnaireId, answers);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.feedbackById._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useScoreCardFeedbackSubmitResults = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ employeeId, questionnaireId, answers }: SubmitParams) {
      const result = await apiScoreCard.submitResults(employeeId, questionnaireId, answers);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: scoreCardsKeys.feedbackById._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeeScoreCardFormByFeedback = ({
  employeeId,
  feedbackId,
  enabled = true,
}: {
  employeeId: number;
  feedbackId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    ...scoreCardsKeys.formByFeedback(employeeId, feedbackId),
    enabled: enabled,
  });
};

export const useEmployeeScoreCardResultsForFeedback = ({
  employeeId,
  feedbackId,
  enabled = true,
}: {
  employeeId: number;
  feedbackId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    ...scoreCardsKeys.resultsForFeedback(employeeId, feedbackId),
    enabled: enabled,
  });
};

export const useCandidateFeedbacksOverview = ({
  candidateId,
  enabled,
}: {
  candidateId: number;
  enabled: boolean;
}) => {
  return useQuery({
    ...scoreCardsKeys.candidateFeedbacksOverview(candidateId),
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};

export const useCandidateFeedbackResendInvite = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({
      candidateId,
      feedbackId,
    }: {
      candidateId: number;
      feedbackId: number;
    }) {
      const result = await apiScoreCard.resendInviteFeedback(candidateId, feedbackId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
